import {
  attach,
  combine,
  createDomain,
  createEffect,
  createEvent,
  createStore,
  sample,
} from "effector";
import { createGate } from "effector-react";
import { campaignsApi } from "../api";
import { articlesApi } from "../../articles/api";
import { CampaignDetails, CampaignError } from "../types";
import { notifications } from "@mantine/notifications";
import { interval } from "patronum";
import { articlesEntityModel } from "../../articles/model";
import { campaignEntityModel } from "./list";
import { exportCampaignReportToCsv } from "../utils/export-campaign-report-to-csv";
import { viewerEntity } from "../../../shared/entities/viewer";

const Gate = createGate<{
  id?: number;
}>();

const domain = createDomain();

const fetchDataFx = createEffect(campaignsApi.getById);
const getDetailsForExportFx = createEffect(async (id: number) => {
  const campaign = await campaignsApi.getById(id);

  for (const task of campaign.tasks) {
    const article = await articlesApi.getById(task.id);
    task.similaritiesUrls = article?.similarities?.map((item) => (
      item.url
    ));
  }

  return campaign;
});

const deleteCampaignFx = createEffect(campaignsApi.deleteCampaignById);

const $data = domain.createStore<CampaignDetails | null>(null);
const $exportData = domain.createStore<CampaignDetails | null>(null);

const $currentCampaignId = domain.createStore<number | null>(null);

const exportToCsv = createEvent<{ campaignId: number }>();

const $errors = createStore<CampaignError[]>([]);

const $isFetched = domain
  .createStore(false)
  .on(fetchDataFx.finally, () => true);

const $isLoading = combine(
  {
    isFetched: $isFetched,
    pending: fetchDataFx.pending,
  },
  ({ isFetched, pending }) => !isFetched && pending
);

const getCampaignDetails = createEvent();

sample({
  clock: Gate.open,
  fn: (params) => params.id ?? null,
  target: $currentCampaignId,
});

sample({
  clock: [Gate.status, $currentCampaignId],
  target: getCampaignDetails,
});

sample({
  clock: [getCampaignDetails, articlesEntityModel.deleteArticleByIdFx.doneData],
  source: {
    campaignId: $currentCampaignId,
  },
  filter: ({ campaignId }) => Boolean(campaignId),
  fn: ({ campaignId }) => {
    return campaignId!;
  },
  target: fetchDataFx,
});

fetchDataFx.doneData.watch((data) => {
  console.log("data", data);
});

sample({
  clock: fetchDataFx.doneData,
  target: $data,
});

sample({
  clock: $data,
  fn: (data) => Number(data?.id) ?? null,
  target: $currentCampaignId,
});

const deleteCampaignById = createEvent<{ id: number; email: string, isSuperAdmin : boolean }>();

sample({
  clock: deleteCampaignById,
  source: {
    profile: viewerEntity.$profile,
  },
  filter: ({ profile }, payload) => payload?.isSuperAdmin === true || (Boolean(profile) && payload.email === profile?.email),
  fn: (_, payload) => payload.id,
  target: deleteCampaignFx,
});

sample({
  clock: deleteCampaignById,
  source: {
    profile: viewerEntity.$profile,
  },
  filter: ({ profile }, payload) => ((payload?.isSuperAdmin !== true) && (payload.email !== profile?.email)),
  fn: () =>
    notifications.show({
      message: "You are not creator of this campaign",
      color: "red",
    }),
});

sample({
  clock: [deleteCampaignFx.doneData],
  target: [campaignEntityModel.getList],
});

sample({
  clock: fetchDataFx.doneData,
  filter: (id) => Boolean(id),
  fn: (data) => data?.id,
  target: articlesEntityModel.getList,
});

deleteCampaignFx.done.watch(() => {
  notifications.show({
    title: "Campaign deleted",
    message: "Campaign deleted successfully",
  });
});

sample({
  clock: exportToCsv,
  fn: ({ campaignId }) => campaignId,
  filter: (campaignId) => Boolean(campaignId),
  target: getDetailsForExportFx,
});

const exportToCsvFx = attach({
  source: $exportData,
  effect: (data) => {
    if (!data?.tasks) throw new Error("No data to export");

    return exportCampaignReportToCsv(data);
  },
});

sample({
  clock: getDetailsForExportFx.doneData,
  target: $exportData,
});

sample({
  clock: $exportData,
  target: exportToCsvFx,
});

interval({
  start: Gate.open,
  stop: Gate.close,
  timeout: 10000,
}).tick.watch(getCampaignDetails);

domain.onCreateStore((store) => store.reset(Gate.close));

export const campaignDetailsEntityModel = {
  $data,
  Gate,
  deleteCampaignFx,
  deleteCampaignById,
  $currentCampaignId,
  $isLoading,
  getCampaignDetails,
  exportToCsv,
  $errors,
};
